/**
 * English strings — and the source of truth for the *shape* of every translation.
 *
 * `hu.ts` is typed against this object, so adding a key here without adding it there is a
 * compile error rather than a blank space on the Hungarian site. Keep the keys flat; the
 * nesting is in the naming, not the structure.
 *
 * `{placeholders}` are substituted by `t()`.
 */
export const en = {
  'site.title': 'Pálinka Calculator',
  'site.tagline': 'Calculators and guides for fruit brandy distilling',
  'site.description':
    'Free browser-based calculators for dilution, blending, alcoholmeter temperature correction and mash yield, with guides for fruit brandy distillers.',

  'nav.home': 'Home',
  'nav.calculators': 'Calculators',
  'nav.guides': 'Guides',
  'nav.language': 'Language',
  'nav.skipToContent': 'Skip to content',
  /**
   * Label for the light/dark button. Deliberately names the action rather than the state —
   * the button is rendered at build time and the theme is only known in the browser, so a
   * state-naming label ("Dark theme on") would be a lie until JavaScript corrected it.
   */
  'nav.toggleTheme': 'Switch between light and dark',

  'common.results': 'Results',
  'common.method': 'Method and accuracy',
  'common.reset': 'Reset',
  'common.readMore': 'Read more',
  'common.backToGuides': 'Back to guides',
  'common.backToCalculators': 'Back to calculators',
  'common.sourceOnGitHub': 'Source on GitHub',
  /**
   * Localised, not shared: Hungarian writes the family name first, so the same person is
   * `Fuksz Domonkos` on the Hungarian pages and `Domonkos Fuksz` here. Bump the year when
   * the site next changes substantially — it is deliberately a literal, not a build-time
   * value, so a rebuild never silently alters a legal notice.
   */
  'common.copyright': '© 2024–2026 Domonkos Fuksz',
  /** Appended to a calculator's name to title its own page, e.g. "Dilution calculator". */
  'common.calculatorWord': 'calculator',
  'common.breadcrumb': 'Breadcrumb',
  'common.relatedGuide': 'Related guide',
  'common.relatedCalculator': 'Related calculator',
  'common.otherCalculators': 'Other calculators',
  'common.updated': 'Updated',
  'common.disclaimer':
    'These calculators are for planning and education. They are not valid for excise, customs or any legal declaration.',
  'common.liveRegionLabel': 'Calculation results',

  'unit.percent': '% ABV',
  'unit.litre': 'l',
  'unit.celsius': '°C',
  'unit.kilogram': 'kg',

  'error.notANumber': 'Enter a number.',
  'error.abvRange': 'Alcohol content must be between 0 and 100 %ABV.',
  'error.targetAboveSource':
    'The target strength cannot exceed the current {abv} %ABV — adding water only makes a spirit weaker.',
  'error.volumePositive': 'Volume must be greater than zero.',
  'error.volumeNonNegative': 'Volume cannot be negative.',
  'error.massPositive': 'Mass must be greater than zero.',
  'error.percentRange': 'Enter a percentage between 0 and 100.',
  'error.readingOutOfRange':
    'This correction is only reliable between {min} and {max} %ABV. Outside that range, use the official tables.',
  'error.tempOutOfRange':
    'This correction is only reliable between {min} and {max} °C. Outside that range, use the official tables.',

  'notes.idealMixing':
    'Assumes ideal mixing. Ethanol and water contract slightly when combined — most strongly around 50 %ABV — so real volumes run a little lower than calculated, by up to about 2–3%. Add slightly less water than the figure shown, measure, then top up.',
  'notes.tempApproximation':
    'Uses a linear approximation of 0.33 %ABV per °C away from 20 °C. The true correction also depends on the strength itself, so expect a few tenths of a percent of error. For anything official, use the OIML alcoholometric tables.',
  'notes.absoluteAlcohol':
    'This calculation is exact — no mixing is involved, so the only uncertainty is how accurately you measured the strength. Reporting thresholds and allowances vary by country and change over time; this tells you a quantity, not your obligations.',
  'notes.sugarYield':
    'A planning estimate. The efficiency figure absorbs everything real fermentation and distillation lose: yeast growth, unfermentable residue, alcohol left in the pot, and the heads and tails you discard. Measure your own output and calibrate the efficiency to your setup.',

  // `calc.<id>.slug` gives each calculator its own URL segment, localised so Hungarian
  // readers get Hungarian URLs. Changing one changes a published address — treat it as a
  // rename, not a tweak.
  'calc.dilution.slug': 'dilution',
  'calc.dilution.name': 'Dilution',
  'calc.dilution.description':
    'How much water to add to bring a spirit down to the strength you want.',
  'calc.dilution.field.volume': 'Volume of spirit',
  'calc.dilution.field.abv': 'Current strength',
  'calc.dilution.field.targetAbv': 'Target strength',
  'calc.dilution.output.waterToAdd': 'Water to add',
  'calc.dilution.output.finalVolume': 'Final volume',
  'calc.dilution.output.absoluteAlcohol': 'Pure alcohol (unchanged)',

  'calc.blend.slug': 'blending',
  'calc.blend.name': 'Blending',
  'calc.blend.description': 'The strength you get when two spirits are combined.',
  'calc.blend.field.volumeA': 'Volume of the first',
  'calc.blend.field.abvA': 'Strength of the first',
  'calc.blend.field.volumeB': 'Volume of the second',
  'calc.blend.field.abvB': 'Strength of the second',
  'calc.blend.output.abv': 'Resulting strength',
  'calc.blend.output.totalVolume': 'Total volume',
  'calc.blend.output.absoluteAlcohol': 'Pure alcohol',

  'calc.tempCorrection.slug': 'temperature-correction',
  'calc.tempCorrection.name': 'Temperature correction',
  'calc.tempCorrection.description':
    'Alcoholmeters are calibrated at 20 °C. Correct a reading taken at any other temperature.',
  'calc.tempCorrection.field.reading': 'Alcoholmeter reading',
  'calc.tempCorrection.field.temperature': 'Temperature of the spirit',
  'calc.tempCorrection.output.correctedAbv': 'True strength at 20 °C',
  'calc.tempCorrection.output.correction': 'Correction applied',
  'calc.tempCorrection.output.deviation': 'Deviation from 20 °C',

  'calc.absoluteAlcohol.slug': 'absolute-alcohol',
  'calc.absoluteAlcohol.name': 'Absolute alcohol',
  'calc.absoluteAlcohol.description':
    'The litres of pure ethanol in a quantity of spirit — the unit records and duties are counted in.',
  'calc.absoluteAlcohol.field.volume': 'Volume of spirit',
  'calc.absoluteAlcohol.field.abv': 'Strength',
  'calc.absoluteAlcohol.output.absoluteAlcohol': 'Absolute alcohol',
  'calc.absoluteAlcohol.output.equivalentAt50': 'Equivalent at 50 %ABV',
  'calc.absoluteAlcohol.output.ethanolMass': 'Mass of ethanol',

  'calc.sugarYield.slug': 'mash-yield',
  'calc.sugarYield.name': 'Mash yield',
  'calc.sugarYield.description':
    'Roughly how much spirit a fruit mash will give, from its sugar content.',
  'calc.sugarYield.field.mashMass': 'Mass of mash',
  'calc.sugarYield.field.sugarContent': 'Sugar content',
  'calc.sugarYield.field.efficiency': 'Overall efficiency',
  'calc.sugarYield.output.spiritAt50': 'Finished spirit at 50 %ABV',
  'calc.sugarYield.output.pureEthanol': 'Pure alcohol',
  'calc.sugarYield.output.sugarMass': 'Fermentable sugar',
  'calc.sugarYield.output.theoreticalMax': 'Theoretical maximum',

  'page.home.title': 'Pálinka Calculator',
  'page.home.intro':
    'Practical calculators for fruit brandy distilling — dilution, blending, alcoholmeter correction and mash yield — plus guides explaining the arithmetic behind them. Everything runs in your browser; nothing you type is sent anywhere.',
  'page.home.calculatorsHeading': 'Calculators',
  'page.home.guidesHeading': 'Guides',

  'page.calculators.title': 'Calculators',
  'page.calculators.intro':
    'Every calculator states its method and its limits. Where a formula is an approximation, it says so.',

  'page.guides.title': 'Guides',
  'page.guides.intro': 'The reasoning behind the calculators, and how to use them well.',

  'page.notFound.title': 'Page not found',
  'page.notFound.intro':
    'There is nothing at this address. It may have been renamed, or the link that brought you here may be wrong.',
  'page.notFound.backHome': 'Go to the home page',
} as const;

export type TranslationKey = keyof typeof en;
export type Translations = Record<TranslationKey, string>;
